<template>
  <div>
    <tippy
      theme="statzon--filter"
      :interactive="true"
      :visible="visible"
      placement="bottom"
      :distance="windowWidth < 640 ? 5 : 11"
      animation="scale"
      trigger="click"
      arrow
      arrowType="round"
      :maxWidth="windowWidth < 1200 ? '704px' : '744px'"
      :onShow="openSelection"
      :onHide="closeSelection"
    >
      <template v-slot:trigger>
        <FormButton
          text="Add filter"
          size="sm"
          variant="dark"
          :circle="true"
          :borderless="true"
          color="blue"
          icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6 13h-5v5h-2v-5h-5v-2h5v-5h2v5h5v2z"/></svg>'
          iconSize="md"
          :selected="selectingFilter"
        />
      </template>

      <div>
        <form
          ref="filterSelection"
          @keyup.enter="addFilter()"
          v-on:submit.prevent=""
        >
          <div class="selection">
            <div class="selection__content">
              <div
                :class="['selection__info', errorMessage && !selecting ? 'selection__info--error' : '']"
                v-html="errorMessage && !selecting ? errorMessage : getTip"
              />

              <div class="selection__options">
                <div class="selection__option">
                  <FormSelect
                    :value="field"
                    :options="getFields"
                    type="select"
                    size="md"
                    variant="light"
                    :noLabels="true"
                    :rounded="false"
                    :squared="true"
                    :placeholder="field ? field.name : 'Select field'"
                    refName="field"
                    :scrollPosition="0"
                    :iconSize="this.config.icons.field.iconSize"
                    :icon="this.config.icons.field.icon"
                    :error="errors.field"
                    :status="status.field"
                    @focused="selecting = true"
                    @unfocused="selecting = false"
                    @positionChanged="setScrollPosition"
                    @tipChanged="setSelectedTip"
                    @valueChanged="setSelectedField"
                  />
                </div>

                <div class="selection__option">
                  <FormSelect
                    :value="condition"
                    :options="getConditions"
                    type="select"
                    size="md"
                    variant="light"
                    :noLabels="true"
                    :rounded="false"
                    :squared="true"
                    :placeholder="condition ? condition.name : 'Condition'"
                    refName="condition"
                    :scrollPosition="0"
                    :iconSize="this.config.icons.condition.iconSize"
                    :icon="this.config.icons.condition.icon"
                    :error="errors.condition"
                    :status="status.condition"
                    @focused="selecting = true"
                    @unfocused="selecting = false"
                    @positionChanged="setScrollPosition"
                    @tipChanged="setSelectedTip"
                    @valueChanged="setSelectedCondition"
                  />
                </div>

                <div class="selection__option">
                  <FormSelect
                    v-if="condition.key === 'is' || condition.key === 'ns'"
                    :value="criteria"
                    :options="getSuggestions()"
                    type="suggestions"
                    size="md"
                    variant="light"
                    :autofocus="true"
                    :noLabels="true"
                    :rounded="false"
                    :squared="true"
                    :placeholder="getCriteriaPlaceholder"
                    refName="criteriaSelect"
                    :scrollPosition="0"
                    :iconSize="this.config.icons.criteria.iconSize"
                    :icon="this.config.icons.criteria.icon"
                    :error="errors.criteria"
                    :status="status.criteria"
                    :loading="loading"
                    :timeout="timeout"
                    :noResults="noResults"
                    @focused="selecting = true"
                    @unfocused="selecting = false"
                    @positionChanged="setScrollPosition"
                    @inputChanged="fetchSuggestions"
                    @valueChanged="setSelectedCriteria"
                  />

                  <FormInput
                    v-else
                    :value="criteria.name"
                    :key="'CRITERIA__' + field.key ? field.key : 'DEFAULT'"
                    type="text"
                    refName="criteriaInput"
                    :placeholder="getCriteriaPlaceholder"
                    size="md"
                    variant="light"
                    :noLabels="true"
                    :rounded="false"
                    :squared="true"
                    :iconSize="this.config.icons.criteria.iconSize"
                    :icon="this.config.icons.criteria.icon"
                    :error="errors.criteria"
                    :status="status.criteria"
                    @focused="selecting = true"
                    @unfocused="selecting = false"
                    @inputChanged="setSelectedCriteria"
                  />
                </div>

                <div class="selection__option selection__option--button">
                  <FormButton
                    variant="light"
                    color="blue"
                    icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M6 16c1.361-5.928 8-7 8-7v-2l4 3.982-4 4.018v-2s-5.102-.104-8 3zm-4-4c0 5.514 4.486 10 10 10s10-4.486 10-10-4.486-10-10-10-10 4.486-10 10zm22 0c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12z"/></svg>'
                    iconSize="md"
                    :borderless="true"
                    :selected="false"
                    :disabled="false"
                    style="border-left: 4px solid rgba(255,255,255,0.1);"
                    @click.native="addFilter()"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </tippy>
  </div>
</template>

<script>
import FormSelect from '@/components/form/Select.vue'
import FormInput from '@/components/form/Input.vue'
import FormButton from '@/components/form/Button.vue'
import FormSubmit from '@/components/form/Submit.vue'
import UtilityButton from '@/components/utility/Button.vue'
import configFile from '@/helpers/config.js'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    FormSelect,
    FormInput,
    FormButton,
    FormSubmit,
    UtilityButton
  },
  props: {
    //
  },
  data () {
    return {
      config: configFile,
      loading: false,
      timeout: false,
      noResults: false,
      visible: false,
      formStatus: 'default',
      field: false,
      condition: false,
      criteria: false,
      fieldScrollPosition: false,
      selectedTip: false,
      selecting: false,
      errorMessage: false,
      windowWidth: window.innerWidth,
      // TODO: Move to searchControls
      accessOptions: [
        { 'key': 'free', 'name': 'Free' },
        { 'key': 'available', 'name': 'Available' },
        { 'key': 'locked', 'name': 'Locked' },
        { 'key': false, 'name': 'Everything' }
      ],
      errors: {
        field: false,
        condition: false,
        criteria: false
      },
      status: {
        field: false,
        condition: false,
        criteria: false
      }
    }
  },
  computed: {
    suggestions () {
      return this.$store.getters['search/getSuggestions']
    },
    suggestionsMeta () {
      return this.$store.getters['search/getSuggestionsMeta']
    },
    input () {
      return this.value
    },
    selectingFilter () {
      return this.$store.getters['search/getSelectingFilter']
    },
    selectedFilters () {
      return this.$store.getters['search/getSelectedFilters']
    },
    type () {
      let type = ['Search', 'Datasets', 'PDFs', 'News'].includes(this.$route.name) ? this.$route.name.toLowerCase() : 'search'
      return type
    },
    getConditions () {
      let conditions = []
      let availableConditions = []
      let selectedCondition = false

      if (this.field.key !== false && this.config.filterFields[this.type]) {
        this.config.filterFields[this.type].forEach(field => {
          if (field.key === this.field.key) {
            availableConditions = field.conditions
            selectedCondition = field.selected
          }
        })
      }

      if (availableConditions.length > 0) {
        this.config.filterConditions.forEach(condition => {
          if (availableConditions.includes(condition.key)) {
            if (this.config.filterNames[condition.key]) {
              condition['key'] = this.config.filterNames[condition.key].toLowerCase()
              condition['name'] = this.config.filterNames[condition.key]
            }

            if (this.config.tips[condition.key]) {
              condition['tip'] = this.config.tips[condition.key]
            }

            if (this.config.icons[condition.key]) {
              condition['icon'] = this.config.icons[condition.key]['icon']
              condition['iconSize'] = this.config.icons[condition.key]['iconSize']
            }

            if (selectedCondition === condition.key) {
              condition['selected'] = true
              this.condition = condition
            } else {
              condition['selected'] = false
            }

            conditions.push(condition)
          }
        })
      }

      return conditions
    },
    getFields () {
      let fields = this.config.filterFields[this.type]
      let formattedFields = []

      if (fields.length > 0) {
        fields.forEach(field => {
          let formattedField = {}

          if (this.config.filterNames[field.key]) {
            formattedField['key'] = field.key

            if (field.key === 'titles' && this.type === 'search') {
              formattedField['name'] = this.config.filterNames[field.key] + ' / Name'
            } else {
              formattedField['name'] = this.config.filterNames[field.key]
            }
          }

          if (this.config.tips[field.key]) {
            formattedField['tip'] = this.config.tips[field.key]
          }

          if (this.config.icons[field.key]) {
            formattedField['icon'] = this.config.icons[field.key]['icon']
            formattedField['iconSize'] = this.config.icons[field.key]['iconSize']
          }

          formattedFields.push(formattedField)
        })
      }

      return formattedFields
    },
    getCriteriaPlaceholder () {
      let placeholder = 'Criteria'

      if (['is', 'ns'].includes(this.condition.key)) {
        placeholder = 'Type to search'
      } else if (this.field.key === 'published') {
        placeholder = 'YYYY-MM-DD'
      }

      return placeholder
    },
    getTip () {
      let tip = 'Add filters to refine or narrow down search results.'

      if (this.fieldScrollPosition !== false && this.fieldScrollPosition !== -1) {
        tip = this.selectedTip
      }

      else if (this.field && this.field.tip) {
        tip = this.field.tip
      }

      return tip
    }
  },
  methods: {
    fetchSuggestions: helperMethods.debounce(
      async function (searchQuery) {
        if (searchQuery.length === 0 || (searchQuery.length >= 2 && searchQuery.length <= 64)) {
          this.loading = true
          let type = this.$route.name.toLowerCase()
          let searchString = searchQuery.length !== 0 ? 'search=' + searchQuery : ''

          if (!['datasets', 'pdfs', 'news'].includes(type)) {
            type = 'all'
          }

          let url = 'suggestions?' + searchString + '&type=' + type + '&match=' + this.field.key + '&' + this.$store.getters['search/getFiltersQuery'](false, false) + this.$store.getters['search/getControlsQuery']('search', false) + '&size=10&page=1'
          this.$store.dispatch('search/setSearchToken', { 'type': 'suggestions', 'url': url })

          if (!this.suggestions || !this.searchToken || this.searchToken.suggestions !== this.suggestionsMeta.parameters.search_token) {
            this.$store.dispatch('search/fetchSuggestions', url).then(response => {
              this.loading = false

              if (this.suggestions.allIds.length === 0) {
                this.noResults = true
                this.timeout = false
              } else {
                this.noResults = false
                this.timeout = false
              }

              return true
            }).catch(error => {
              this.timeout = true
              this.loading = false
            })
          } else {
            this.noResults = false
            this.timeout = false
            this.loading = false
          }
        }

        return false
      },
      250
    ),
    getSuggestions () {
      let formattedFields = []

      if (this.suggestions && this.suggestions.allIds && this.suggestions.allIds.length > 0) {
        this.suggestions.allIds.forEach(function (suggestionId) {
          let formattedField = {
            'key': this.suggestions.byId[suggestionId].id,
            'name': this.suggestions.byId[suggestionId].attributes.name,
            'results': this.suggestions.byId[suggestionId].attributes.results,
            'icon': this.config.icons.criteria.icon,
            'iconSize': this.config.icons.criteria.iconSize
          }

          formattedFields.push(formattedField)
        }.bind(this))
      }

      return formattedFields
    },
    validate (field) {
      let valid = true

      if (field === 'field' || field === 'all') {
        if (this.field === false) {
          this.errorMessage = 'Field is required.'
          this.errors.field = true
          valid = false
        } else if (this.config.validFilters.includes(this.field.key) === false) {
          this.errorMessage = 'Invalid field.'
          this.errors.field = true
          valid = false
        } else {
          this.errorMessage = false
          this.errors.field = false
        }

        this.status.field = this.errors.field ? 'invalid' : 'valid'
      }

      if (field === 'condition' || field === 'all') {
        if (this.condition === false) {
          this.errorMessage = 'Condition is required.'
          this.errors.condition = true
          valid = false
        } else if (this.config.validConditions.includes(this.condition.key) === false) {
          this.errorMessage = 'Invalid condition.'
          this.errors.condition = true
          valid = false
        } else {
          this.errorMessage = false
          this.errors.condition = false
        }

        this.status.condition = this.errors.condition ? 'invalid' : 'valid'
      }

      if (field === 'criteria' || field === 'all') {
        if (this.criteria === false) {
          this.errorMessage = 'Criteria is required.'
          this.errors.criteria = true
          valid = false
        } else if (this.criteria.key.length < 2) {
          this.errorMessage = 'Criteria has to be minimum of 2 characters long.'
          this.errors.criteria = true
          valid = false
        } else if (this.criteria.key.length > 64) {
          this.errorMessage = 'Criteria can be maximum of 64 characters long.'
          this.errors.criteria = true
          valid = false
        } else if (this.field.key === 'published' && ((this.criteria.key.length !== 4) && (this.criteria.key.length !== 10 || !this.criteria.key.includes('-')) && (this.criteria.key.length !== 7 || !this.criteria.key.includes('-'))) ) {
          this.errorMessage = 'Date must be in YYYY-MM-DD format.'
          this.errors.criteria = true
          valid = false
        } else {
          this.errorMessage = false
          this.errors.criteria = false
        }

        this.status.criteria = this.errors.criteria ? 'invalid' : 'valid'
      }

      return valid
    },
    addFilter () {
      if (this.validate('all') === true) {
        let filters = this.selectedFilters
        let newFilter = {
          'type': this.field.key.trim(),
          'id': this.criteria.key.trim(),
          'attributes': {
            'name': encodeURIComponent(this.criteria.name.trim()),
            'condition': this.condition.key.trim()
          }
        }

        // TODO: Trigger errors from these
        if (this.itemFound(newFilter, filters[this.field.key])) {
          let key = this.itemFound(newFilter, filters[this.field.key])
          filters[this.field.key].splice(key, 1)
        } else if (!filters[this.field.key]) {
          filters[this.field.key] = []
          filters[this.field.key].push(newFilter)
        } else if (filters[this.field.key].length < 25) {
          filters[this.field.key].push(newFilter)
        }

        this.closeSelection()
        this.$store.dispatch('search/setSelectedFilters', filters)
        this.$store.dispatch('search/setPageNumber', { type: 'datasets', value: 1 })
        this.$store.dispatch('search/setPageNumber', { type: 'pdfs', value: 1 })
        this.$store.dispatch('search/setPageNumber', { type: 'news', value: 1 })
        this.$router.push('/' + this.type + '?' + this.$store.getters['search/getFiltersQuery'](false, false))
      }
    },
    setSelectedField (item) {
      this.field = item
      this.validate('field')

      this.getConditions.forEach(condition => {
        if (condition.selected === true) {
          this.setSelectedCondition(condition)
        }
      })
    },
    setSelectedCondition (item) {
      this.condition = item
      this.validate('condition')

      if (this.validate('condition') === true) {
        this.$nextTick(() => {
          this.criteria = false

          if (this.$refs.filterSelection['criteriaInput']) {
            this.$refs.filterSelection['criteriaInput'].focus()
          } else if (this.$refs.filterSelection['criteriaSelect']) {
            this.fetchSuggestions('')
            this.$refs.filterSelection['criteriaSelect'].focus()
          }
        })
      }
    },
    setSelectedCriteria (item) {
      if (!item.key) {
        this.criteria = {
          'key': item,
          'name': item
        }
      } else {
        this.criteria = item
      }

      if ((this.condition.key === 'is' || this.condition.key === 'ns') && this.validate('criteria') === true) {
        this.addFilter()
      }
    },
    setScrollPosition (key) {
      this.fieldScrollPosition = key
    },
    setSelectedTip (tip) {
      this.selectedTip = tip
    },
    openSelection () {
      this.scrollPosition = 0
      this.field = false
      this.condition = false
      this.criteria = false
      this.errors = {
        field: false,
        condition: false,
        criteria: false
      }
      this.status = {
        field: false,
        condition: false,
        criteria: false
      }

      this.$store.dispatch('search/setSuggestions', [])
      this.$store.dispatch('search/setSelectingFilter', true)
    },
    closeSelection () {
      this.visible = false
      this.$store.dispatch('search/setSelectingFilter', false)
      this.$emit('selectionClosed')
    },
    resize () {
      this.windowWidth = window.innerWidth
    },
    ...helperMethods
  },
  created () {
    window.addEventListener('resize', this.resize)
  },
  unmounted () {
    window.removeEventListener('resize', this.resize)
  },
  mounted () {
    this.resize()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.selection {
  @include font('content--md', $white, 0.8);
  width: 100%;
  min-width: calc(100vw - 24px);
  padding: 0;

  @media screen and (min-width: $breakpointSm) {
    width: 616px;
    min-width: auto;
  }

  @media screen and (min-width: $breakpointMd) {
    padding: 0 32px 32px 32px;
  }

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white, 0.7);
    width: 680px;
    padding: 0 32px 32px 32px;
  }

  &__content {
    background-color: $bg-2;
    border-left: 1px solid $black;
    border-right: 1px solid $black;
    border-bottom: 1px solid $black;
  }

  &__info {
    border-bottom: 1px solid $black;
    padding: 8px;
    color: rgba($white, 0.9);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--error {
      color: $red;
    }
  }

  &__options {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    height: 40px;
    border-top: 1px solid rgba($white, 0.15);

    @media screen and (min-width: $breakpointSm) {
      flex-wrap: nowrap;
    }

    @media screen and (min-width: $breakpointLg) {
      height: 42px;
    }
  }

  &__option {
    display: flex;
    width: 100%;
    border-bottom: 1px solid $black;

    @media screen and (min-width: $breakpointSm) {
      border-bottom: none;

      &:last-child {
        width: 100px;
      }
    }

    &--button div {
      width: 100%;
      height: 34px;

      @media screen and (min-width: $breakpointSm) {
        height: auto;
      }
    }
  }
}

</style>
